/** IOFlow's CustomEdge (`../IOFlow/frontend/src/components/flow/CustomEdge.js`)
    in TypeScript: a soft bezier with its label in a chip, the error branch red
    and dashed. Wizard routes add their own tones — a case amber, a repeat body
    purple, a call's return indigo, a read-only route (an op / tool goto, edited
    in the inspector) dotted grey, and "the following step" (no `next` written)
    dashed grey. */

import { memo } from 'react'
import { BaseEdge, EdgeLabelRenderer, getBezierPath } from '@xyflow/react'
import type { Edge, EdgeProps } from '@xyflow/react'
import type { RouteEdge } from './graph'
import { PORT_TONE } from './nodes'

export interface RouteEdgeData extends Record<string, unknown> {
  route: RouteEdge
}

export type WizardFlowEdge = Edge<RouteEdgeData, 'wzRoute'>

function strokeOf(route: RouteEdge): { stroke: string; dash?: string } {
  if (route.readonly) return { stroke: 'var(--color-text-faint, #687281)', dash: '2 4' }
  if (route.implicit) return { stroke: 'var(--color-text-faint, #687281)', dash: '6 4' }
  if (route.tone === 'issue') return { stroke: PORT_TONE.issue, dash: '6 4' }
  if (route.tone === 'else') return { stroke: PORT_TONE.else, dash: '5 3' }
  return { stroke: PORT_TONE[route.tone as keyof typeof PORT_TONE] || PORT_TONE.next }
}

export const RouteEdgeView = memo(function RouteEdgeView({
  id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, markerEnd, data, selected,
}: EdgeProps<WizardFlowEdge>) {
  const route = data?.route
  const [path, labelX, labelY] = getBezierPath({
    sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition, curvature: 0.25,
  })
  const { stroke, dash } = route ? strokeOf(route) : { stroke: PORT_TONE.next, dash: undefined }
  const label = route?.label
  return (
    <>
      <BaseEdge id={id} path={path} markerEnd={markerEnd} style={{
        stroke: selected ? 'var(--color-accent)' : stroke,
        strokeWidth: selected ? 3 : 2,
        strokeDasharray: dash,
        transition: 'stroke 0.2s ease, stroke-width 0.2s ease',
      }} />
      {label && (
        <EdgeLabelRenderer>
          <div className="nodrag nopan" style={{
            position: 'absolute', transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            pointerEvents: 'all', fontSize: 13, padding: '1px 6px', borderRadius: 4, whiteSpace: 'nowrap',
            maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis',
            background: 'var(--color-bg-secondary)', border: `1px solid ${route?.tone === 'issue' ? PORT_TONE.issue : 'var(--color-border)'}`,
            color: route?.tone === 'issue' ? PORT_TONE.issue : 'var(--color-text-secondary)',
            fontStyle: route?.readonly || route?.implicit ? 'italic' : undefined,
          }} title={route?.readonly ? `${label} — edit it in the step's inspector` : label}>
            {label}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  )
})

export const edgeTypes = { wzRoute: RouteEdgeView }
